import { useQuery } from '@tanstack/react-query';
import { getProjectWarehouses } from '../services/api/implementation/impl';

/**
 * One project (and warehouse) choice shared by the inventory screens: Stock,
 * Pipeline, Site receipts, the inventory dashboard, reports, Warehouses and
 * Transfers. Picking a project on one of them carries over to the others.
 *
 * Kept in localStorage; every access is guarded because storage can be
 * blocked (private windows, cleared site data).
 */
const PROJECT_KEY = 'inventory_selected_project';
const WAREHOUSE_KEY = 'inventory_selected_warehouse'; // { [projectId]: warehouseId | '' }

// Each page used to keep its own choice; read those once so nobody loses theirs.
const LEGACY_PROJECT_KEYS = [
  'stock_selected_project',
  'inventory_pipeline_project',
  'inventory_site_receipts_project',
  'inventory_consumption_project',
  'inventory_escalation_project'
];

const read = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // storage blocked: the choice just isn't remembered
  }
};

/**
 * The remembered project id, if it is one of `projects` (when given).
 * Returns a number or null.
 */
export function getSavedInventoryProjectId(projects) {
  const candidates = [read(PROJECT_KEY), ...LEGACY_PROJECT_KEYS.map(read)];
  for (const raw of candidates) {
    const id = parseInt(raw, 10);
    if (!Number.isFinite(id) || id <= 0) continue;
    if (!projects || projects.some((p) => Number(p.id) === id)) return id;
  }
  return null;
}

export function saveInventoryProjectId(projectId) {
  const id = parseInt(projectId, 10);
  if (Number.isFinite(id) && id > 0) write(PROJECT_KEY, String(id));
}

/** The remembered warehouse for a project: a number, or '' for all warehouses. */
export function getSavedWarehouseId(projectId) {
  try {
    const map = JSON.parse(read(WAREHOUSE_KEY) || '{}');
    const value = map?.[String(projectId)];
    return value ? Number(value) : '';
  } catch {
    return '';
  }
}

export function saveWarehouseId(projectId, warehouseId) {
  if (!projectId) return;
  let map = {};
  try {
    map = JSON.parse(read(WAREHOUSE_KEY) || '{}') || {};
  } catch {
    map = {};
  }
  map[String(projectId)] = warehouseId ? Number(warehouseId) : '';
  write(WAREHOUSE_KEY, JSON.stringify(map));
}

/**
 * A project's warehouses (default first, then active, then by name).
 * `active` = active ones only; `defaultWarehouse` = the project's default;
 * `hasMany` = more than one active warehouse, i.e. a warehouse choice matters.
 */
export function useProjectWarehouses(projectId) {
  const query = useQuery({
    queryKey: ['projectWarehouses', projectId ? Number(projectId) : projectId],
    queryFn: () => getProjectWarehouses(projectId),
    enabled: !!projectId,
    retry: false,
    staleTime: 60 * 1000
  });
  const warehouses = query.data || [];
  const active = warehouses.filter((w) => w.is_active !== false);
  return {
    ...query,
    warehouses,
    active,
    defaultWarehouse: warehouses.find((w) => w.is_default) || null,
    hasMany: active.length > 1
  };
}

/** A remembered warehouse is only used while it is still an active warehouse of the project. */
export function validWarehouseId(warehouseId, activeWarehouses) {
  if (!warehouseId) return '';
  return (activeWarehouses || []).some((w) => Number(w.id) === Number(warehouseId))
    ? Number(warehouseId)
    : '';
}
